// packages/shared-components/src/components/Modal/Modal.tsx

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { getSubAppPortalContainer } from "@eweadn-qiankun/shared-utils";
import { Iconfont } from "../Iconfont";
import { ModalProps } from "./types";
import "./Modal.css";

export const Modal = ({
  visible,
  title,
  closable = true,
  footer = true,
  okText,
  cancelText,
  onOk,
  onCancel,
  onClose,
  maskClosable = true,
  className,
  width = 520,
  containerRef,
  animated = true,
  portalContainer,
  children,
}: ModalProps) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!animated) {
      setIsVisible(visible);
      setIsAnimating(visible);
      document.body.style.overflow = visible ? "hidden" : "";
      return;
    }

    if (visible) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.body.style.overflow = "hidden";
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = window.setTimeout(() => {
        setIsVisible(false);
        document.body.style.overflow = "";
      }, 300);
      return () => window.clearTimeout(timer);
    }
  }, [visible, isVisible, animated]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      onCancel?.();
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const handleOk = () => {
    onOk?.();
  };

  const handleMaskClick = (e: React.MouseEvent) => {
    if (maskClosable && e.target === e.currentTarget) {
      handleClose();
    }
  };

  const instantClass = animated ? "" : "shared-modal--no-animation";

  const modalContent = (
    <div
      className={`shared-modal-mask ${isAnimating ? "is-open" : ""} ${instantClass}`}
      onClick={handleMaskClick}
    >
      <div
        ref={containerRef}
        className={`shared-modal-container ${isAnimating ? "is-open" : ""} ${instantClass} ${
          className || ""
        }`}
        style={{ width }}
      >
        {(title || closable) && (
          <div className="shared-modal-header">
            {title && <h3 className="shared-modal-title">{title}</h3>}
            {closable && (
              <button
                type="button"
                className="shared-modal-close"
                onClick={handleClose}
                aria-label={t("common.close")}
              >
                <Iconfont icon="close" className="shared-modal-close-icon" />
              </button>
            )}
          </div>
        )}

        <div className="shared-modal-body">{children}</div>

        {footer && (
          <div className="shared-modal-footer">
            {cancelText !== "" && (
              <button
                className="shared-modal-btn-cancel"
                onClick={handleCancel}
              >
                {cancelText || t("common.cancel")}
              </button>
            )}
            <button className="shared-modal-btn-ok" onClick={handleOk}>
              {okText || t("common.confirm")}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (!isVisible) return null;

  const mountNode =
    portalContainer ??
    (typeof document !== "undefined" ? getSubAppPortalContainer() : null);
  if (!mountNode) return null;

  return createPortal(modalContent, mountNode);
};
